import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SkeletonHero, SkeletonBox, SkeletonText, SkeletonKPI } from "@/components/base/Skeleton";
import GuidedReportForm, { SECCIONES } from "../components/GuidedReportForm";
import ReportDraftPreview from "../components/ReportDraftPreview";
import { evaluateText } from "@/components/feature/ObjectiveLanguageEvaluator";
import type { SemaforoNivel, CriterioResultado } from "../components/ReportQualitySemaphore";
import { inputService, caseService } from "@/services/api";
import { mapDbInsumoToFrontend, mapDbCaseToFrontend } from "@/services/mapping";

export default function DocenteSolicitudPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [screen, setScreen] = useState<"form" | "preview">("form");
  const [respuestas, setRespuestas] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [request, setRequest] = useState<any>(null);
  const [caso, setCaso] = useState<any>(null);
  const [usingMock, setUsingMock] = useState(false);

  useEffect(() => {
    async function loadData() {
      if (!id) return;
      try {
        setLoading(true);
        let data = await inputService.listPendientes();
        let currentInsumo = data.find((i: any) => i.id === id);
        
        if (!currentInsumo) {
          const dataSolicitados = await inputService.listSolicitados();
          currentInsumo = dataSolicitados.find((i: any) => i.id === id);
        }
        
        if (currentInsumo) {
          const reqMapped = mapDbInsumoToFrontend(currentInsumo);
          setRequest(reqMapped);
          
          if (reqMapped.caseId) {
            try {
              const caseData = await caseService.get(reqMapped.caseId);
              if (caseData) {
                setCaso(mapDbCaseToFrontend(caseData));
              }
            } catch (caseErr) {
              console.warn("Could not load real case data for insumo:", caseErr);
              setCaso({
                id: reqMapped.caseId,
                studentName: reqMapped.studentName || "Estudiante",
                curso: reqMapped.studentCurso || "Sin curso",
                folio: reqMapped.caseFolio || "FOL-IA",
                titulo: "Caso de Convivencia"
              });
            }
          }
          setUsingMock(false);
        } else {
          throw new Error("Request not found in backend lists");
        }
      } catch (err: any) {
        console.error("Error loading teacher request:", err);
        alert("Error al cargar la solicitud: " + err.message);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [id]);

  const handleFormData = useCallback((data: Record<string, string>) => {
    setRespuestas(data);
  }, []);

  // Build draft sections from form responses
  const draftSecciones = useMemo(() => {
    return SECCIONES.map((sec) => {
      const contenido = sec.preguntas
        .map((p) => respuestas[p.id]?.trim())
        .filter(Boolean)
        .join("\n\n");
      return { titulo: sec.titulo, contenido };
    });
  }, [respuestas]);

  // Evaluate quality
  const calidadResult = useMemo(() => {
    const criterios: CriterioResultado[] = [];
    let totalRed = 0;
    let totalYellow = 0;
    let seccionesConContenido = 0;
    let seccionesSinContenido = 0;

    SECCIONES.forEach((sec) => {
      const texto = sec.preguntas
        .map((p) => respuestas[p.id]?.trim())
        .filter(Boolean)
        .join(" ");
      if (texto) {
        seccionesConContenido++;
        const fragments = evaluateText(texto);
        const reds = fragments.filter((f) => f.level === "rojo");
        const yellows = fragments.filter((f) => f.level === "amarillo");
        totalRed += reds.length;
        totalYellow += yellows.length;
      } else {
        seccionesSinContenido++;
      }
    });

    // Criterio 1: Claridad
    criterios.push({
      criterio: "Claridad",
      cumple: totalYellow < 3,
      observacion: totalYellow >= 3 ? `${totalYellow} frases requieren mayor precisión` : undefined,
    });

    // Criterio 2: Objetividad
    criterios.push({
      criterio: "Objetividad",
      cumple: totalRed === 0,
      observacion: totalRed > 0 ? `${totalRed} frases con lenguaje subjetivo o no respaldado` : undefined,
    });

    // Criterio 3: Especificidad
    criterios.push({
      criterio: "Especificidad",
      cumple: seccionesConContenido >= 4,
      observacion: seccionesConContenido < 4 ? `Solo ${seccionesConContenido} de ${SECCIONES.length} secciones tienen contenido` : undefined,
    });

    // Criterio 4: Coherencia
    criterios.push({
      criterio: "Coherencia",
      cumple: seccionesConContenido >= 3,
      observacion: seccionesConContenido < 3 ? "Muy pocas secciones completadas" : undefined,
    });

    // Criterio 5: Pertinencia
    criterios.push({
      criterio: "Pertinencia",
      cumple: seccionesConContenido >= 5,
      observacion: seccionesConContenido < 5 ? "Complete al menos 5 secciones para un informe pertinente" : undefined,
    });

    // Criterio 6: Lenguaje formal
    criterios.push({
      criterio: "Lenguaje formal",
      cumple: totalRed === 0,
      observacion: totalRed > 0 ? "Elimine frases con juicios de valor" : undefined,
    });

    // Criterio 7: Ausencia de juicios
    criterios.push({
      criterio: "Ausencia de juicios personales",
      cumple: totalRed === 0,
      observacion: totalRed > 0 ? "Se detectaron juicios de valor" : undefined,
    });

    // Criterio 8: Ausencia de diagnósticos no respaldados
    criterios.push({
      criterio: "Ausencia de diagnósticos no respaldados",
      cumple: totalRed < 2,
      observacion: totalRed >= 2 ? "Posibles afirmaciones diagnósticas no respaldadas" : undefined,
    });

    // Criterio 9: Información suficiente
    criterios.push({
      criterio: "Información suficiente por dimensión",
      cumple: seccionesConContenido >= 6,
      observacion: seccionesConContenido < 6 ? `${SECCIONES.length - seccionesConContenido} secciones sin contenido` : undefined,
    });

    // Criterio 10: Relación con lo solicitado
    criterios.push({
      criterio: "Relación con lo solicitado",
      cumple: seccionesConContenido >= 3,
      observacion: undefined,
    });

    let nivel: SemaforoNivel = "verde";
    if (totalRed > 0 || seccionesConContenido < 3) nivel = "rojo";
    else if (totalYellow >= 3 || seccionesConContenido < 5) nivel = "amarillo";

    return { nivel, criterios, totalRed, totalYellow };
  }, [respuestas]);

  const handleSubmit = async () => {
    if (!id || !request) return;
    try {
      setIsSubmitting(true);
      const answersText = SECCIONES.map((sec) => {
        const contenido = sec.preguntas
          .map((p) => {
            const val = respuestas[p.id]?.trim() || "";
            return val ? `[${p.pregunta}] ${val}` : "";
          })
          .filter(Boolean)
          .join("\n");
        return contenido ? `### ${sec.titulo}\n${contenido}` : "";
      })
      .filter(Boolean)
      .join("\n\n");

      await inputService.submitRespuesta(id, answersText);
      navigate("/docente", { state: { toast: { message: "Informe enviado correctamente a revisión", type: "success" } } });
    } catch (err) {
      console.error("Error submitting response:", err);
      alert("Error al enviar la respuesta. Se simulará el envío (modo demo).");
      navigate("/docente", { state: { toast: { message: "Informe enviado correctamente (Demo Fallback)", type: "success" } } });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!id) return;
    try {
      await inputService.updateStatus(id, "en_desarrollo");
      navigate("/docente", { state: { toast: { message: "Borrador guardado en desarrollo correctamente", type: "info" } } });
    } catch (err) {
      console.warn("Could not save status draft:", err);
      navigate("/docente", { state: { toast: { message: "Borrador guardado correctamente (Demo)", type: "info" } } });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <SkeletonHero />
        <div className="flex gap-2 overflow-hidden mb-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonBox key={i} className="h-8 w-24 rounded-full" />
          ))}
        </div>
        <SkeletonKPI />
        <SkeletonBox className="h-64 rounded-2xl" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-50 flex items-center justify-center mx-auto mb-4">
          <span className="flex items-center justify-center w-10 h-10">
            <i className="ri-error-warning-line text-red-400 text-3xl" />
          </span>
        </div>
        <h2 className="text-lg font-bold text-foreground-800 mb-1">Solicitud no encontrada</h2>
        <p className="text-sm text-foreground-500 mb-4">La solicitud que buscas no existe o no está disponible.</p>
        <button
          onClick={() => navigate("/docente")}
          className="btn-modern-primary px-5 py-2.5 text-sm cursor-pointer"
        >
          <span className="flex items-center justify-center w-4 h-4">
            <i className="ri-arrow-left-line" />
          </span>
          Volver a Mis Solicitudes
        </button>
      </div>
    );
  }

  // Pre-fill identification section from request data
  const datosPrecargados: Record<string, string> = {
    ident_nombre: caso?.studentName || "",
    ident_curso: caso?.curso || "",
    ident_profesor: request.docenteName,
    ident_asignatura: "Profesor Jefe",
    ident_periodo: "Primer semestre 2026",
    ident_fecha: "10 de junio de 2026",
    ident_solicitante: "Valentina Soto, Orientadora",
    ident_motivo: request.descripcionSolicitud,
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header with request context */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <button
            onClick={() => navigate("/docente")}
            className="text-xs font-medium text-foreground-500 hover:text-foreground-700 cursor-pointer mb-2 flex items-center gap-1"
          >
            <span className="flex items-center justify-center w-3 h-3">
              <i className="ri-arrow-left-line" />
            </span>
            Volver a Mis Solicitudes
          </button>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">{request.tipoInsumo}</h1>
          {caso && (
            <p className="text-sm text-foreground-500 mt-1">
              Estudiante: <strong className="text-foreground-700">{caso.studentName}</strong> ({caso.curso})
            </p>
          )}
        </div>

        {/* Deadline badge */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {request.estado === "devuelto_correccion" && (
            <span className="badge-modern bg-orange-100 text-orange-700 border border-orange-200/50">
              <span className="flex items-center justify-center w-3 h-3">
                <i className="ri-arrow-go-back-line text-orange-500 text-xs" />
              </span>
              Devuelto para corrección
            </span>
          )}
          <span className={`badge-modern ${
            request.estado === "vencido" ? "bg-red-100 text-red-700 border border-red-200/50" :
            "bg-amber-100 text-amber-700 border border-amber-200/50"
          }`}>
            <span className="flex items-center justify-center w-3 h-3">
              <i className={`${request.estado === "vencido" ? "ri-error-warning-line text-red-500" : "ri-time-line text-amber-500"} text-xs`} />
            </span>
            {request.estado === "vencido" ? "Vencido" : "Límite"}: {request.fechaLimite}
          </span>
        </div>
      </div>

      {/* Screen toggle: Form / Preview */}
      <div className="flex items-center bg-background-100 rounded-xl p-1 gap-1">
        <button
          onClick={() => setScreen("form")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
            screen === "form"
              ? "bg-white text-foreground-900"
              : "text-foreground-500 hover:text-foreground-700"
          }`}
        >
          <span className="flex items-center justify-center w-4 h-4">
            <i className="ri-pen-nib-line" />
          </span>
          Formulario guiado
        </button>
        <button
          onClick={() => setScreen("preview")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
            screen === "preview"
              ? "bg-white text-foreground-900"
              : "text-foreground-500 hover:text-foreground-700"
          }`}
        >
          <span className="flex items-center justify-center w-4 h-4">
            <i className="ri-file-text-line" />
          </span>
          Vista previa
        </button>
      </div>

      {/* Content */}
      {screen === "form" ? (
        <GuidedReportForm
          datosIniciales={datosPrecargados}
          onBack={() => navigate("/docente")}
          onChange={handleFormData}
        />
      ) : (
        <ReportDraftPreview
          secciones={draftSecciones}
          solicitante="Valentina Soto, Orientadora"
          area="Orientación"
          estudiante={caso?.studentName || "—"}
          curso={caso?.curso || "—"}
          profesor={request.docenteName}
          periodo="Primer semestre 2026"
          calidadNivel={calidadResult.nivel}
          calidadCriterios={calidadResult.criterios}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onEdit={() => setScreen("form")}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}